import { useMemo, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import {
  Button,
  CardHeader,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  Field,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SectionHeader,
  SegmentedControl,
  StatTile,
  TextInput,
  cn,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  ACCOUNT_KIND,
  BALANCE_SOURCE,
  CASH_SCENARIO,
  ENTRY_DIRECTION,
  accountKindOf,
  benefitsSource,
  calibratedDate,
  calibratedNumber,
  formatMoney,
  formatPercent,
  formatRate,
  latestBalance,
  longDate,
  looksLikeEmail,
  manualAccountId,
  overdueBills,
  PARAMETER_SOURCE,
  parseMoney,
  parsePercent,
  parsePayrollForm,
  payrollFormFrom,
  payrollRateSource,
  todayYmd,
  useAssumptions,
  useCashFlow,
  useSaveAssumptions,
  useSaveBankBalances,
  ymd,
  type AccountKind,
  type AssumptionsView,
  type BalanceSource,
  type BankBalanceEntry,
  type CashAccountConfig,
  type CashAssumptions,
  type EntryDirection,
  type OpenBill,
  type PayrollBreakdown,
  type PayrollErrors,
  type PayrollForm,
} from '@/lib/cash-flow';
import { filterRows, sortRows } from '@/lib/facet-table';
import { ACCOUNTING_PATH } from '@/lib/finance';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

const SOURCE_OPTIONS = [
  { key: BALANCE_SOURCE.manual, label: 'Manual' },
  { key: BALANCE_SOURCE.quickbooks, label: 'QuickBooks' },
] as const;

const DIRECTION_OPTIONS = [
  { key: ENTRY_DIRECTION.in, label: 'In' },
  { key: ENTRY_DIRECTION.out, label: 'Out' },
] as const;

const KIND_LABEL: Readonly<Record<AccountKind, string>> = {
  [ACCOUNT_KIND.bank]: 'Bank account',
  [ACCOUNT_KIND.creditCard]: 'Credit card, enter the balance owed',
};

const savedAt = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

/** The confirmation a form turns into once it has saved. */
function SavedNote({ message }: { readonly message: string }): React.ReactElement {
  return (
    <p role="status" className="rounded-portal-card bg-portal-success-bg p-3 text-portal-small font-bold text-portal-success">
      {message}
    </p>
  );
}

// ─── Bank balances ───────────────────────────────────────────────────────────

/** One row of the balance form: a tracked account, or a QuickBooks account not in the forecast yet. */
interface BalanceRow {
  readonly accountId: string;
  readonly name: string;
  readonly kind: AccountKind;
  readonly tracked: boolean;
  readonly source: BalanceSource;
  readonly inQuickBooks: boolean;
  readonly quickbooksBalance: { readonly amount: number; readonly asOf: string } | null;
  readonly lastEntry: { readonly amount: number; readonly asOf: string } | null;
}

function balanceRows(view: AssumptionsView): BalanceRow[] {
  const qbo = new Map(view.quickbooksAccounts.map((account) => [account.accountId, account]));
  const tracked = view.assumptions.accounts.map((account): BalanceRow => {
    const fromQbo = qbo.get(account.accountId);
    const last = latestBalance(view.assumptions.balanceEntries, account.accountId);
    return {
      accountId: account.accountId,
      name: account.name,
      kind: account.kind,
      tracked: true,
      source: account.source,
      inQuickBooks: fromQbo !== undefined,
      quickbooksBalance: fromQbo ? { amount: fromQbo.currentBalance, asOf: fromQbo.asOf } : null,
      lastEntry: last ? { amount: last.amount, asOf: last.asOf } : null,
    };
  });
  const trackedIds = new Set(tracked.map((row) => row.accountId));
  const untracked = view.quickbooksAccounts
    .filter((account) => account.active && !trackedIds.has(account.accountId))
    .map(
      (account): BalanceRow => ({
        accountId: account.accountId,
        name: account.name,
        kind: accountKindOf(account),
        tracked: false,
        source: BALANCE_SOURCE.manual,
        inQuickBooks: true,
        quickbooksBalance: { amount: account.currentBalance, asOf: account.asOf },
        lastEntry: null,
      }),
    );
  return [...tracked, ...untracked];
}

function BankBalancesPanel({ view }: { readonly view: AssumptionsView }): React.ReactElement {
  const rows = useMemo(() => balanceRows(view), [view]);
  const [asOf, setAsOf] = useState(todayYmd());
  const [amounts, setAmounts] = useState<Readonly<Record<string, string>>>({});
  const [sources, setSources] = useState<Readonly<Record<string, BalanceSource>>>({});
  const [invalid, setInvalid] = useState<readonly string[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const saveBalances = useSaveBankBalances();
  const saveAccounts = useSaveAssumptions();
  const pending = saveBalances.isPending || saveAccounts.isPending;
  const error = saveBalances.error ?? saveAccounts.error;

  const sourceOf = (row: BalanceRow): BalanceSource => sources[row.accountId] ?? row.source;
  const changedSources = rows.filter((row) => row.tracked && sourceOf(row) !== row.source);
  const typed = rows.filter((row) => (amounts[row.accountId] ?? '').trim() !== '');
  const dirty = typed.length > 0 || changedSources.length > 0;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const bad = typed.filter((row) => parseMoney(amounts[row.accountId] ?? '') === null).map((row) => row.accountId);
    setInvalid(bad);
    if (bad.length > 0 || !dirty) return;
    setSaved(null);
    let latest: AssumptionsView | undefined;
    if (changedSources.length > 0) {
      const accounts: CashAccountConfig[] = view.assumptions.accounts.map((account) => ({
        ...account,
        source: sources[account.accountId] ?? account.source,
      }));
      latest = await saveAccounts.mutateAsync({ accounts });
    }
    if (typed.length > 0) {
      const entries: BankBalanceEntry[] = typed.map((row) => ({
        accountId: row.accountId,
        amount: parseMoney(amounts[row.accountId] ?? '') ?? 0,
        asOf,
        ...(row.tracked ? {} : { name: row.name, kind: row.kind }),
      }));
      latest = await saveBalances.mutateAsync(entries);
    }
    setAmounts({});
    setSources({});
    const parts = [
      typed.length > 0 ? `${typed.length} ${typed.length === 1 ? 'balance' : 'balances'} saved` : null,
      changedSources.length > 0 ? `${changedSources.length} ${changedSources.length === 1 ? 'source' : 'sources'} switched` : null,
    ].filter((part): part is string => part !== null);
    setSaved(`${parts.join(', ')}. Cash today is now ${formatMoney(latest?.opening.total ?? view.opening.total)}.`);
  }

  return (
    <Panel className="p-5">
      <CardHeader
        title="Bank balances"
        description="Type each account's balance from the bank site. Blank accounts keep their last entry."
        aside={<span className="tabular-nums">Cash today {formatMoney(view.opening.total)}</span>}
      />
      {rows.length === 0 ? (
        <EmptyState
          title="No accounts to enter"
          message="QuickBooks has not sent any bank or card accounts yet. They appear here after the next finance sync."
        />
      ) : (
        <form onSubmit={(event) => void submit(event)} className="space-y-4">
          <Field label="Balances as of" htmlFor="balances-as-of" hint="The date on the bank site, usually today." required className="max-w-xs">
            <DatePicker id="balances-as-of" value={asOf} onChange={setAsOf} max={todayYmd()} />
          </Field>
          <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {rows.map((row) => {
              const inputId = `balance-${row.accountId}`;
              const last = row.lastEntry;
              const isBad = invalid.includes(row.accountId);
              return (
                <li key={row.accountId} className="grid gap-3 p-3 md:grid-cols-[minmax(0,1fr)_240px_220px] md:items-center">
                  <div className="min-w-0">
                    <label htmlFor={inputId} className="block text-portal-small font-bold text-portal-ink">
                      {row.name}
                    </label>
                    <span className="block text-portal-micro text-portal-muted">
                      {KIND_LABEL[row.kind]}
                      {last ? ` · last entry ${formatMoney(last.amount)} as of ${longDate(last.asOf)}` : ' · no entry yet'}
                    </span>
                    <span className="block text-portal-micro text-portal-muted">
                      {row.quickbooksBalance
                        ? `QuickBooks ${formatMoney(row.quickbooksBalance.amount)} as of ${longDate(row.quickbooksBalance.asOf)}`
                        : 'Not in QuickBooks'}
                    </span>
                    {row.tracked ? null : (
                      <Pill tone="neutral" className="mt-1">
                        Not in the forecast yet: a balance adds it
                      </Pill>
                    )}
                  </div>
                  <div>
                    <TextInput
                      id={inputId}
                      inputMode="decimal"
                      placeholder={last ? String(last.amount) : '0'}
                      value={amounts[row.accountId] ?? ''}
                      aria-invalid={isBad || undefined}
                      disabled={sourceOf(row) === BALANCE_SOURCE.quickbooks}
                      onChange={(event) => setAmounts((current) => ({ ...current, [row.accountId]: event.target.value }))}
                      className="text-right tabular-nums"
                    />
                    {isBad ? <span className="mt-1 block text-portal-micro text-rose-700">Enter a dollar amount</span> : null}
                  </div>
                  <div>
                    {row.tracked && row.inQuickBooks ? (
                      <SegmentedControl<BalanceSource>
                        label={`Where ${row.name}'s balance comes from`}
                        value={sourceOf(row)}
                        onChange={(next) => setSources((current) => ({ ...current, [row.accountId]: next }))}
                        options={SOURCE_OPTIONS}
                      />
                    ) : (
                      <span className="text-portal-micro text-portal-muted">Manual</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-portal-micro text-portal-muted">
            QuickBooks balances are not trusted until the books reconcile. Keep each account on Manual until then.
          </p>
          {error ? <ErrorNotice inline title="Could not save balances" message={errorMessage(error)} /> : null}
          {saved && !dirty ? <SavedNote message={saved} /> : null}
          <Button type="submit" disabled={!dirty || pending}>
            {pending ? 'Saving…' : 'Save balances'}
          </Button>
        </form>
      )}
    </Panel>
  );
}

// ─── Forecast settings ───────────────────────────────────────────────────────

interface SettingsForm {
  readonly minimumCash: string;
  readonly adBudget: string;
  readonly eventCostRatio: string;
  readonly contractToSignedRate: string;
  readonly cancellationRate: string;
  readonly downsideHaircut: string;
}

const money = (value: number | null): string => (value === null ? '' : String(value));
const percent = (share: number | null): string => (share === null ? '' : String(Math.round(share * 1000) / 10));

function settingsFrom(assumptions: CashAssumptions): SettingsForm {
  return {
    minimumCash: money(assumptions.minimumCash),
    adBudget: money(assumptions.adBudgetPerWeek),
    eventCostRatio: percent(assumptions.eventCostRatio),
    contractToSignedRate: percent(assumptions.contractToSignedRate),
    cancellationRate: percent(assumptions.cancellationRate),
    downsideHaircut: percent(assumptions.downsideHaircut),
  };
}

type SettingsErrors = Partial<Record<keyof SettingsForm, string>>;

/** Turns the typed form into the PUT body, or the per-field errors. */
function parseSettings(form: SettingsForm) {
  const errors: SettingsErrors = {};
  const required = (key: keyof SettingsForm, parse: (text: string) => number | null, message: string): number => {
    const value = parse(form[key]);
    if (value === null) errors[key] = message;
    return value ?? 0;
  };
  const optional = (key: keyof SettingsForm, parse: (text: string) => number | null, message: string): number | null => {
    if (form[key].trim() === '') return null;
    const value = parse(form[key]);
    if (value === null) errors[key] = message;
    return value;
  };
  const body = {
    minimumCash: required('minimumCash', parseMoney, 'Enter a dollar amount'),
    adBudgetPerWeek: optional('adBudget', parseMoney, 'Enter a dollar amount'),
    eventCostRatio: optional('eventCostRatio', parsePercent, 'Enter a percent from 0 to 100'),
    contractToSignedRate: optional('contractToSignedRate', parsePercent, 'Enter a percent from 0 to 100'),
    cancellationRate: optional('cancellationRate', parsePercent, 'Enter a percent from 0 to 100'),
    downsideHaircut: required('downsideHaircut', parsePercent, 'Enter a percent from 0 to 100'),
  };
  return { body, errors };
}

function SettingsPanel({
  assumptions,
  parameters,
}: {
  readonly assumptions: CashAssumptions;
  readonly parameters: Readonly<Record<string, unknown>> | undefined;
}): React.ReactElement {
  const savedForm = useMemo(() => settingsFrom(assumptions), [assumptions]);
  const [form, setForm] = useState<SettingsForm>(savedForm);
  const [errors, setErrors] = useState<SettingsErrors>({});
  const save = useSaveAssumptions();
  const dirty = (Object.keys(form) as (keyof SettingsForm)[]).some((key) => form[key] !== savedForm[key]);

  const ads = calibratedNumber(parameters, 'adSpendWeekly');
  const costRatio = calibratedNumber(parameters, 'eventCostRatio');
  const contractRate = calibratedNumber(parameters, 'contractToSigned');
  const cancellation = calibratedNumber(parameters, 'cancellationRate');
  /** "from 1,138 deals over 2 years", only for a measured value. */
  const dealsNote = (rate: typeof contractRate, unit: string): string | undefined =>
    rate && rate.source === PARAMETER_SOURCE.history
      ? `from ${rate.sampleSize.toLocaleString('en-US')} ${unit} over 2 years`
      : undefined;
  const measuredNote = (label: string, value: string | undefined, sample?: string): string =>
    value === undefined ? `Blank uses the ${label}.` : `Blank uses the ${label}: ${value}${sample ? `, ${sample}` : ''}.`;

  function set<K extends keyof SettingsForm>(key: K): (event: React.ChangeEvent<HTMLInputElement>) => void {
    return (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    const { body, errors: found } = parseSettings(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    save.mutate(body);
  }

  const fieldError = (key: keyof SettingsForm): React.ReactNode =>
    errors[key] ? <span className="mt-1 block text-portal-micro text-rose-700">{errors[key]}</span> : null;

  return (
    <Panel className="p-5">
      <CardHeader
        title="Forecast settings"
        description="The levers the forecast uses. Leave an override blank to use the value measured from history."
      />
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Field label="Minimum cash" htmlFor="minimum-cash" hint="Weeks closing below this are flagged. Two payroll runs." required>
            <TextInput id="minimum-cash" inputMode="decimal" value={form.minimumCash} onChange={set('minimumCash')} />
            {fieldError('minimumCash')}
          </Field>
          <Field
            label="Downside haircut (%)"
            htmlFor="downside-haircut"
            hint="How much less the downside case expects to collect."
            required
          >
            <TextInput id="downside-haircut" inputMode="decimal" value={form.downsideHaircut} onChange={set('downsideHaircut')} />
            {fieldError('downsideHaircut')}
          </Field>
          <Field
            label="Ad budget per week"
            htmlFor="ad-budget"
            hint={measuredNote('recent weekly ad spend', ads ? formatMoney(ads.value) : undefined)}
          >
            <TextInput id="ad-budget" inputMode="decimal" value={form.adBudget} onChange={set('adBudget')} />
            {fieldError('adBudget')}
          </Field>
          <Field
            label="Event cost ratio (%)"
            htmlFor="event-cost-ratio"
            hint={measuredNote(
              'measured ratio',
              costRatio ? formatPercent(costRatio.value) : undefined,
              costRatio && costRatio.sampleSize > 0 ? `from ${costRatio.sampleSize} events` : undefined,
            )}
          >
            <TextInput id="event-cost-ratio" inputMode="decimal" value={form.eventCostRatio} onChange={set('eventCostRatio')} />
            {fieldError('eventCostRatio')}
          </Field>
          <Field
            label="Contracts that sign (%)"
            htmlFor="contract-to-signed"
            hint={`Deals with a contract out count at this share in the base case. ${measuredNote(
              'measured rate',
              contractRate ? formatRate(contractRate.value) : undefined,
              dealsNote(contractRate, 'deals'),
            )}`}
          >
            <TextInput
              id="contract-to-signed"
              inputMode="decimal"
              value={form.contractToSignedRate}
              onChange={set('contractToSignedRate')}
            />
            {fieldError('contractToSignedRate')}
          </Field>
          <Field
            label="Signed events that cancel (%)"
            htmlFor="cancellation-rate"
            hint={`Events not held yet are cut by this share. ${measuredNote(
              'measured rate',
              cancellation ? formatRate(cancellation.value) : undefined,
              dealsNote(cancellation, 'signed deals'),
            )}`}
          >
            <TextInput id="cancellation-rate" inputMode="decimal" value={form.cancellationRate} onChange={set('cancellationRate')} />
            {fieldError('cancellationRate')}
          </Field>
        </div>
        {save.isError ? <ErrorNotice inline title="Could not save settings" message={errorMessage(save.error)} /> : null}
        {save.isSuccess && !dirty ? (
          <SavedNote message={`Saved ${savedAt(assumptions.updatedAt)}. The forecast now uses these settings.`} />
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
          {save.isSuccess && !dirty ? (
            <Button variant="secondary" to={ACCOUNTING_PATH.cashFlow}>
              See the forecast
            </Button>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}

// ─── Payroll ─────────────────────────────────────────────────────────────────

/** "Now $57,729: estimated from transfers less commissions, the latest 2 runs." */
function usedNow(value: number | undefined, source: string | undefined): string {
  if (value === undefined) return '';
  const said = source ? `: ${source.charAt(0).toLowerCase()}${source.slice(1)}` : '';
  return `Now ${formatMoney(value)}${said}.`;
}

function PayrollPanel({
  assumptions,
  breakdown,
  parameters,
}: {
  readonly assumptions: CashAssumptions;
  readonly breakdown: PayrollBreakdown | undefined;
  readonly parameters: Readonly<Record<string, unknown>> | undefined;
}): React.ReactElement {
  const savedForm = useMemo(() => payrollFormFrom(assumptions), [assumptions]);
  const [form, setForm] = useState<PayrollForm>(savedForm);
  const [errors, setErrors] = useState<PayrollErrors>({});
  const save = useSaveAssumptions();
  const dirty = (Object.keys(form) as (keyof PayrollForm)[]).some((key) => form[key] !== savedForm[key]);
  const anchor = calibratedDate(parameters, 'payrollAnchor');
  const commissions = breakdown?.commissions;
  const commissionsSource =
    commissions === undefined
      ? undefined
      : commissions.source === PARAMETER_SOURCE.override
        ? 'set here'
        : commissions.source === PARAMETER_SOURCE.history
          ? `average of Salesforce commission payments over the latest ${commissions.sampleSize} paydays`
          : 'none found in Salesforce yet';

  function set<K extends keyof PayrollForm>(key: K): (event: React.ChangeEvent<HTMLInputElement>) => void {
    return (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    const { body, errors: found } = parsePayrollForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    save.mutate(body);
  }

  const fieldError = (key: keyof PayrollForm): React.ReactNode =>
    errors[key] ? <span className="mt-1 block text-portal-micro text-rose-700">{errors[key]}</span> : null;

  return (
    <Panel className="p-5">
      <CardHeader
        title="Payroll"
        description="Each payday goes out as two lines: the run's wages and payroll taxes, and the commissions paid with it. Benefits go out once a month. Leave a field blank to use the measured value."
      />
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Field
            label="Payroll per run, excluding commissions"
            htmlFor="payroll-per-run"
            hint={`Wages and payroll taxes for one run, from the payroll register. ${usedNow(
              breakdown?.rate.value,
              breakdown ? payrollRateSource(breakdown.rate) : undefined,
            )}`}
          >
            <TextInput id="payroll-per-run" inputMode="decimal" value={form.perRun} onChange={set('perRun')} />
            {fieldError('perRun')}
          </Field>
          <Field
            label="Where the payroll amount came from"
            htmlFor="payroll-note"
            hint="Shown beside the amount on Cash flow, for example the run date in the Paycor register."
          >
            <TextInput id="payroll-note" value={form.perRunNote} onChange={set('perRunNote')} placeholder="Paycor register, Sep 30 run" />
            {fieldError('perRunNote')}
          </Field>
          <Field
            label="Current team since"
            htmlFor="current-team-since"
            hint="When the payroll amount is estimated, it averages the runs from this date. Blank uses the latest two runs."
          >
            <DatePicker
              id="current-team-since"
              value={form.currentTeamSince}
              placeholder="Latest two runs"
              max={todayYmd()}
              onChange={(value) => setForm((current) => ({ ...current, currentTeamSince: value }))}
            />
          </Field>
          <Field
            label="Commissions per payday"
            htmlFor="commissions-per-payday"
            hint={`Paid with payroll on each payday. ${usedNow(commissions?.value, commissionsSource)}`}
          >
            <TextInput id="commissions-per-payday" inputMode="decimal" value={form.commissions} onChange={set('commissions')} />
            {fieldError('commissions')}
          </Field>
          <Field
            label="Benefits per month"
            htmlFor="benefits-monthly"
            hint={`Health, dental and the SIMPLE IRA, as a total. ${usedNow(
              breakdown?.benefits.value,
              breakdown ? benefitsSource(breakdown.benefits) : undefined,
            )}`}
          >
            <TextInput id="benefits-monthly" inputMode="decimal" value={form.benefitsMonthly} onChange={set('benefitsMonthly')} />
            {fieldError('benefitsMonthly')}
          </Field>
          <Field label="Benefits day of month" htmlFor="benefits-day" hint="The day the health insurance debit usually lands." required>
            <TextInput id="benefits-day" inputMode="numeric" value={form.benefitsDay} onChange={set('benefitsDay')} />
            {fieldError('benefitsDay')}
          </Field>
          <Field
            label="A payroll run date"
            htmlFor="payroll-anchor"
            hint={`Runs repeat every two weeks from this date. Blank uses the last run found${anchor ? `: ${longDate(anchor.value)}` : ''}.`}
          >
            <DatePicker
              id="payroll-anchor"
              value={form.anchorDate}
              placeholder="Last run found"
              onChange={(value) => setForm((current) => ({ ...current, anchorDate: value }))}
            />
          </Field>
        </div>
        {save.isError ? <ErrorNotice inline title="Could not save payroll" message={errorMessage(save.error)} /> : null}
        {save.isSuccess && !dirty ? (
          <SavedNote message={`Saved ${savedAt(assumptions.updatedAt)}. The forecast now uses these payroll amounts.`} />
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save payroll'}
          </Button>
          {save.isSuccess && !dirty ? (
            <Button variant="secondary" to={ACCOUNTING_PATH.cashFlow}>
              See the forecast
            </Button>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}

// ─── New bookings ────────────────────────────────────────────────────────────

const SWITCH = {
  on: 'on',
  off: 'off',
} as const;
type Switch = (typeof SWITCH)[keyof typeof SWITCH];

const SWITCH_OPTIONS = [
  { key: SWITCH.on, label: 'On' },
  { key: SWITCH.off, label: 'Off' },
] as const;

const switchOf = (on: boolean): Switch => (on ? SWITCH.on : SWITCH.off);

interface BookingsForm {
  readonly enabled: Switch;
  readonly baseFactor: string;
}

function bookingsFrom(assumptions: CashAssumptions): BookingsForm {
  return {
    enabled: switchOf(assumptions.newBookings.enabled),
    baseFactor: percent(assumptions.newBookings.baseFactor),
  };
}

/** "Measured $38,400 a week from 41 bookings signed in the last 12 weeks." */
function runRateNote(parameters: Readonly<Record<string, unknown>> | undefined): string {
  const runRate = calibratedNumber(parameters, 'newBookingsWeekly');
  if (runRate === undefined) return 'The measured run rate shows here once the forecast loads.';
  const bookings = `${runRate.sampleSize.toLocaleString('en-US')} ${runRate.sampleSize === 1 ? 'booking' : 'bookings'}`;
  return runRate.source === PARAMETER_SOURCE.history
    ? `Measured run rate: ${formatMoney(runRate.value)} a week, from ${bookings} signed in the last 12 weeks.`
    : `Not enough history to measure a run rate (${bookings} signed in the last 12 weeks), so expected bookings have no new bookings yet.`;
}

function NewBookingsPanel({
  assumptions,
  parameters,
}: {
  readonly assumptions: CashAssumptions;
  readonly parameters: Readonly<Record<string, unknown>> | undefined;
}): React.ReactElement {
  const savedForm = useMemo(() => bookingsFrom(assumptions), [assumptions]);
  const [form, setForm] = useState<BookingsForm>(savedForm);
  const [errors, setErrors] = useState<Partial<Record<'baseFactor', string>>>({});
  const save = useSaveAssumptions();
  const dirty = (Object.keys(form) as (keyof BookingsForm)[]).some((key) => form[key] !== savedForm[key]);

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    const baseFactor = parsePercent(form.baseFactor);
    setErrors(baseFactor === null ? { baseFactor: 'Enter a percent from 0 to 100' } : {});
    if (baseFactor === null) return;
    // The downside case has no gravy; its stored factor is passed back unchanged.
    save.mutate({
      newBookings: { enabled: form.enabled === SWITCH.on, baseFactor, downsideFactor: assumptions.newBookings.downsideFactor },
    });
  }

  return (
    <Panel className="p-5">
      <CardHeader
        title="Expected bookings: new bookings"
        description="Expected bookings are cash that is not locked in yet, shown on top of the locked-in closing cash and never in the low-cash emails. New bookings are projected from the weekly run rate of signed bookings, less the deals already counted."
      />
      <p className="mb-4 text-portal-small text-portal-ink">{runRateNote(parameters)}</p>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="In expected bookings" htmlFor="new-bookings-switch" hint="Off leaves the weekly run rate out of expected bookings." required>
            <div id="new-bookings-switch">
              <SegmentedControl<Switch>
                label="New bookings in expected bookings"
                value={form.enabled}
                onChange={(next) => setForm((current) => ({ ...current, enabled: next }))}
                options={SWITCH_OPTIONS}
              />
            </div>
          </Field>
          <Field label="Share of the run rate (%)" htmlFor="new-bookings-base" hint="The usual case counts 80%." required>
            <TextInput
              id="new-bookings-base"
              inputMode="decimal"
              value={form.baseFactor}
              onChange={(event) => setForm((current) => ({ ...current, baseFactor: event.target.value }))}
            />
            {errors.baseFactor ? <span className="mt-1 block text-portal-micro text-rose-700">{errors.baseFactor}</span> : null}
          </Field>
        </div>
        {save.isError ? <ErrorNotice inline title="Could not save new bookings" message={errorMessage(save.error)} /> : null}
        {save.isSuccess && !dirty ? <SavedNote message="Saved. Expected bookings use the new bookings settings now." /> : null}
        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save new bookings'}
        </Button>
      </form>
    </Panel>
  );
}

// ─── Accounts outside QuickBooks ─────────────────────────────────────────────

const KIND_OPTIONS = [
  { key: ACCOUNT_KIND.bank, label: 'Bank account' },
  { key: ACCOUNT_KIND.creditCard, label: 'Credit card' },
] as const;

interface AccountDraft {
  readonly name: string;
  readonly kind: AccountKind;
  readonly balance: string;
  readonly asOf: string;
}

const emptyAccountDraft = (): AccountDraft => ({ name: '', kind: ACCOUNT_KIND.bank, balance: '', asOf: todayYmd() });

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());
}

function ManualAccountsPanel({ view }: { readonly view: AssumptionsView }): React.ReactElement {
  const inQuickBooks = useMemo(() => new Set(view.quickbooksAccounts.map((account) => account.accountId)), [view.quickbooksAccounts]);
  const manual = view.assumptions.accounts.filter((account) => !inQuickBooks.has(account.accountId));
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<AccountDraft>(emptyAccountDraft);
  const [showErrors, setShowErrors] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const add = useSaveBankBalances();
  const remove = useSaveAssumptions();
  const error = add.error ?? remove.error;

  const nameError = draft.name.trim() === '' ? 'Enter the account name' : null;
  const balanceError = parseMoney(draft.balance) === null ? 'Enter a dollar amount' : null;
  const dateError = draft.asOf === '' ? 'Pick the date of the balance' : null;

  function open(): void {
    setDraft(emptyAccountDraft());
    setShowErrors(false);
    setSaved(null);
    setAdding(true);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setShowErrors(true);
    if (nameError || balanceError || dateError) return;
    const name = draft.name.trim();
    await add.mutateAsync([
      {
        accountId: manualAccountId(newId),
        name,
        kind: draft.kind,
        amount: parseMoney(draft.balance) ?? 0,
        asOf: draft.asOf,
      },
    ]);
    setAdding(false);
    setSaved(`${name} added. Its balance counts in cash today.`);
  }

  async function removeAccount(account: CashAccountConfig): Promise<void> {
    setSaved(null);
    await remove.mutateAsync({
      accounts: view.assumptions.accounts.filter((candidate) => candidate.accountId !== account.accountId),
    });
    setSaved(`${account.name} removed from the forecast. Its past balances are kept.`);
  }

  const problem = (message: string | null): React.ReactNode =>
    showErrors && message ? <span className="mt-1 block text-portal-micro text-rose-700">{message}</span> : null;

  return (
    <Panel className="p-5">
      <CardHeader
        title="Accounts outside QuickBooks"
        description="A bank account or card QuickBooks does not have. After adding it, enter its balance each week with the others."
        aside={
          adding ? null : (
            <Button variant="secondary" size="compact" type="button" onClick={open}>
              <PlusIcon aria-hidden className="size-4" />
              Add account
            </Button>
          )
        }
      />
      <div className="space-y-4">
        {manual.length === 0 && !adding ? (
          <EmptyState
            title="No accounts outside QuickBooks"
            message="Add an account here when cash sits somewhere QuickBooks cannot see, like a second bank or a payment app."
            action={
              <Button size="compact" type="button" onClick={open}>
                Add account
              </Button>
            }
          />
        ) : null}
        {manual.length > 0 ? (
          <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {manual.map((account) => {
              const last = latestBalance(view.assumptions.balanceEntries, account.accountId);
              return (
                <li key={account.accountId} className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <span className="block text-portal-small font-bold text-portal-ink">{account.name}</span>
                    <span className="block text-portal-micro text-portal-muted">
                      {account.kind === ACCOUNT_KIND.creditCard ? 'Credit card' : 'Bank account'}
                      {last ? ` · ${formatMoney(last.amount)} as of ${longDate(last.asOf)}` : ' · no balance yet'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="compact"
                    type="button"
                    disabled={remove.isPending}
                    aria-label={`Remove ${account.name}`}
                    onClick={() => void removeAccount(account)}
                  >
                    <Trash2Icon aria-hidden className="size-4" />
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : null}
        {adding ? (
          <form onSubmit={(event) => void submit(event)} className="space-y-4 rounded-portal-card border border-portal-line p-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Account name" htmlFor="manual-account-name" hint="As you would say it, like Truist checking." required>
                <TextInput
                  id="manual-account-name"
                  value={draft.name}
                  onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                />
                {problem(nameError)}
              </Field>
              <Field label="Kind" htmlFor="manual-account-kind" hint="A card's balance owed is subtracted." required>
                <div id="manual-account-kind">
                  <SegmentedControl<AccountKind>
                    label="Kind of account"
                    value={draft.kind}
                    onChange={(next) => setDraft((current) => ({ ...current, kind: next }))}
                    options={KIND_OPTIONS}
                  />
                </div>
              </Field>
              <Field
                label={draft.kind === ACCOUNT_KIND.creditCard ? 'Balance owed' : 'Balance'}
                htmlFor="manual-account-balance"
                hint="From the bank or card site."
                required
              >
                <TextInput
                  id="manual-account-balance"
                  inputMode="decimal"
                  value={draft.balance}
                  onChange={(event) => setDraft((current) => ({ ...current, balance: event.target.value }))}
                  className="text-right tabular-nums"
                />
                {problem(balanceError)}
              </Field>
              <Field label="Balance as of" htmlFor="manual-account-as-of" hint="The date on the site, usually today." required>
                <DatePicker
                  id="manual-account-as-of"
                  value={draft.asOf}
                  max={todayYmd()}
                  onChange={(value) => setDraft((current) => ({ ...current, asOf: value }))}
                />
                {problem(dateError)}
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={add.isPending}>
                {add.isPending ? 'Adding…' : 'Add account'}
              </Button>
              <Button variant="secondary" type="button" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
        {error ? <ErrorNotice inline title="Could not save the account" message={errorMessage(error)} /> : null}
        {saved && !adding ? <SavedNote message={saved} /> : null}
      </div>
    </Panel>
  );
}

// ─── Cash emails ─────────────────────────────────────────────────────────────

function CashEmailsPanel({ assumptions }: { readonly assumptions: CashAssumptions }): React.ReactElement {
  const [enabled, setEnabled] = useState<Switch>(switchOf(assumptions.alertsEnabled));
  const [recipients, setRecipients] = useState<readonly string[]>(assumptions.alertRecipients);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | null>(null);
  const save = useSaveAssumptions();
  const dirty =
    enabled !== switchOf(assumptions.alertsEnabled) ||
    recipients.length !== assumptions.alertRecipients.length ||
    recipients.some((email, index) => email !== assumptions.alertRecipients[index]);

  function addRecipient(): void {
    const email = typed.trim().toLowerCase();
    if (!looksLikeEmail(email)) {
      setTypedError('Enter an email address');
      return;
    }
    if (recipients.includes(email)) {
      setTypedError('Already on the list');
      return;
    }
    setRecipients((current) => [...current, email]);
    setTyped('');
    setTypedError(null);
  }

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    save.mutate({ alertsEnabled: enabled === SWITCH.on, alertRecipients: recipients });
  }

  return (
    <Panel className="p-5">
      <CardHeader
        title="Cash emails"
        description="A summary every Monday after the 6am snapshot, and an alert when one of the next four weeks newly drops below the minimum."
      />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Send cash emails" htmlFor="cash-emails-switch" hint="Off stops the Monday summary and the alerts." required>
          <div id="cash-emails-switch">
            <SegmentedControl<Switch>
              label="Send cash emails"
              value={enabled}
              onChange={setEnabled}
              options={SWITCH_OPTIONS}
            />
          </div>
        </Field>
        <Field
          label="Add a recipient"
          htmlFor="cash-email-recipient"
          hint="Only staff with Accounting access can be added."
        >
          <div className="flex max-w-xl flex-wrap gap-2">
            <TextInput
              id="cash-email-recipient"
              type="email"
              placeholder="name@bestcorporateevents.com"
              value={typed}
              aria-invalid={typedError !== null || undefined}
              onChange={(event) => {
                setTyped(event.target.value);
                setTypedError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addRecipient();
                }
              }}
              className="min-w-0 flex-1"
            />
            <Button variant="secondary" type="button" onClick={addRecipient}>
              Add
            </Button>
          </div>
          {typedError ? <span className="mt-1 block text-portal-micro text-rose-700">{typedError}</span> : null}
        </Field>
        {recipients.length === 0 ? (
          <EmptyState
            title="No one gets the cash emails"
            message="Add at least one person with Accounting access, or nothing is sent."
          />
        ) : (
          <ul aria-label="Recipients" className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {recipients.map((email) => (
              <li key={email} className="flex items-center justify-between gap-3 p-3">
                <span className="min-w-0 truncate text-portal-small text-portal-ink">{email}</span>
                <Button
                  variant="ghost"
                  size="compact"
                  type="button"
                  aria-label={`Remove ${email}`}
                  onClick={() => setRecipients((current) => current.filter((candidate) => candidate !== email))}
                >
                  <Trash2Icon aria-hidden className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {save.isError ? <ErrorNotice inline title="Could not save cash emails" message={errorMessage(save.error)} /> : null}
        {save.isSuccess && !dirty ? <SavedNote message="Saved. The next summary goes out Monday morning." /> : null}
        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save cash emails'}
        </Button>
      </form>
    </Panel>
  );
}

// ─── One-off entries ─────────────────────────────────────────────────────────

interface EntryDraft {
  readonly key: string;
  readonly id?: string;
  readonly date: string;
  readonly label: string;
  readonly direction: EntryDirection;
  readonly amount: string;
}

function draftsFrom(assumptions: CashAssumptions): EntryDraft[] {
  return assumptions.manualEntries.map((entry) => ({
    key: entry.id,
    id: entry.id,
    date: ymd(entry.date),
    label: entry.label,
    direction: entry.direction,
    amount: String(entry.amount),
  }));
}

function sameDrafts(a: readonly EntryDraft[], b: readonly EntryDraft[]): boolean {
  const strip = (draft: EntryDraft) => [draft.id ?? '', draft.date, draft.label, draft.direction, draft.amount].join('|');
  return a.length === b.length && a.every((draft, index) => strip(draft) === strip(b[index] ?? draft) && b[index] !== undefined);
}

function OneOffPanel({ assumptions }: { readonly assumptions: CashAssumptions }): React.ReactElement {
  const savedDrafts = useMemo(() => draftsFrom(assumptions), [assumptions]);
  const [drafts, setDrafts] = useState<EntryDraft[]>(savedDrafts);
  const [showErrors, setShowErrors] = useState(false);
  const [nextKey, setNextKey] = useState(0);
  const save = useSaveAssumptions();
  const dirty = !sameDrafts(drafts, savedDrafts);

  const problem = (draft: EntryDraft): string | null => {
    if (draft.date === '') return 'Pick a date';
    if (draft.label.trim() === '') return 'Say what it is';
    const amount = parseMoney(draft.amount);
    if (amount === null || amount <= 0) return 'Enter an amount above zero';
    return null;
  };

  function update(key: string, patch: Partial<EntryDraft>): void {
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));
  }

  function add(): void {
    setDrafts((current) => [
      ...current,
      { key: `new-${nextKey}`, date: '', label: '', direction: ENTRY_DIRECTION.in, amount: '' },
    ]);
    setNextKey((value) => value + 1);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setShowErrors(true);
    if (drafts.some((draft) => problem(draft) !== null)) return;
    const view = await save.mutateAsync({
      manualEntries: drafts.map((draft) => ({
        ...(draft.id ? { id: draft.id } : {}),
        date: draft.date,
        label: draft.label.trim(),
        direction: draft.direction,
        amount: parseMoney(draft.amount) ?? 0,
      })),
    });
    setDrafts(draftsFrom(view.assumptions));
    setShowErrors(false);
  }

  return (
    <Panel className="p-5">
      <CardHeader
        title="One-off entries"
        description="Known cash the forecast cannot see: a tax payment, a refund, a loan draw, an annual renewal."
        aside={
          <Button variant="secondary" size="compact" type="button" onClick={add}>
            <PlusIcon aria-hidden className="size-4" />
            Add entry
          </Button>
        }
      />
      <form onSubmit={(event) => void submit(event)} className="space-y-4">
        {drafts.length === 0 ? (
          <EmptyState
            title="No one-off entries"
            message="Add any cash in or out on a known date that the forecast would otherwise miss."
            action={
              <Button size="compact" type="button" onClick={add}>
                Add entry
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {drafts.map((draft, index) => {
              const issue = showErrors ? problem(draft) : null;
              return (
                <li
                  key={draft.key}
                  className="grid gap-3 p-3 md:grid-cols-[170px_minmax(0,1fr)_auto_150px_auto] md:items-start"
                >
                  <DatePicker
                    aria-label={`Date of entry ${index + 1}`}
                    value={draft.date}
                    placeholder="Date"
                    onChange={(value) => update(draft.key, { date: value })}
                  />
                  <TextInput
                    aria-label={`What entry ${index + 1} is`}
                    placeholder="What it is"
                    value={draft.label}
                    onChange={(event) => update(draft.key, { label: event.target.value })}
                  />
                  <SegmentedControl<EntryDirection>
                    label={`Direction of entry ${index + 1}`}
                    value={draft.direction}
                    onChange={(next) => update(draft.key, { direction: next })}
                    options={DIRECTION_OPTIONS}
                  />
                  <TextInput
                    aria-label={`Amount of entry ${index + 1}`}
                    inputMode="decimal"
                    placeholder="Amount"
                    value={draft.amount}
                    onChange={(event) => update(draft.key, { amount: event.target.value })}
                    className="text-right tabular-nums"
                  />
                  <Button
                    variant="ghost"
                    size="compact"
                    type="button"
                    aria-label={`Remove entry ${index + 1}`}
                    onClick={() => setDrafts((current) => current.filter((candidate) => candidate.key !== draft.key))}
                  >
                    <Trash2Icon aria-hidden className="size-4" />
                  </Button>
                  {issue ? <span className="text-portal-micro text-rose-700 md:col-span-5">{issue}</span> : null}
                </li>
              );
            })}
          </ul>
        )}
        {save.isError ? <ErrorNotice inline title="Could not save entries" message={errorMessage(save.error)} /> : null}
        {save.isSuccess && !dirty ? <SavedNote message="One-off entries saved. The forecast includes them now." /> : null}
        {drafts.length > 0 || dirty ? (
          <Button type="submit" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save entries'}
          </Button>
        ) : null}
      </form>
    </Panel>
  );
}

// ─── Excluded bills ──────────────────────────────────────────────────────────

const BILL_SORT_FIELDS = {
  vendor: 'vendor',
  billDate: 'billDate',
  dueDate: 'dueDate',
  amount: 'openBalance',
} as const;
const BILL_SORT_KEYS = Object.values(BILL_SORT_FIELDS);
const BILL_DEFAULT_SORT = { key: BILL_SORT_FIELDS.dueDate, direction: 'asc' } as const;
const BILL_SEARCH_FIELDS = ['vendor'] as const satisfies ReadonlyArray<keyof OpenBill>;

function daysOverdue(dueDate: string, today: string): number {
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${ymd(dueDate)}T00:00:00Z`)) / 86_400_000);
}

function ExcludeBox({
  bill,
  checked,
  onToggle,
}: {
  readonly bill: OpenBill;
  readonly checked: boolean;
  readonly onToggle: (id: string, checked: boolean) => void;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-2 text-portal-small">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onToggle(bill.transactionId, event.target.checked)}
        aria-label={`Leave the ${bill.vendor} bill for ${formatMoney(bill.openBalance)} out of the forecast`}
      />
      Exclude
    </label>
  );
}

function ExcludedBillsSection({ view }: { readonly view: AssumptionsView }): React.ReactElement {
  const today = todayYmd();
  const bills = useMemo(() => overdueBills(view.openBills ?? [], today), [view.openBills, today]);
  const savedIds = useMemo(() => new Set(view.assumptions.excludedBillIds), [view.assumptions.excludedBillIds]);
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(savedIds);
  const { sort, onSort } = useTableSort(BILL_SORT_KEYS, BILL_DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const save = useSaveAssumptions();
  const dirty = excluded.size !== savedIds.size || [...excluded].some((id) => !savedIds.has(id));

  const rows = useMemo(() => sortRows(filterRows(bills, search, BILL_SEARCH_FIELDS), sort), [bills, search, sort]);
  const total = bills.reduce((sum, bill) => sum + bill.openBalance, 0);
  const excludedTotal = bills.filter((bill) => excluded.has(bill.transactionId)).reduce((sum, bill) => sum + bill.openBalance, 0);

  function toggle(id: string, checked: boolean): void {
    setExcluded((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const cards: DataTableCard[] = rows.map((bill) => ({
    key: bill.transactionId,
    title: bill.vendor,
    subtitle: bill.dueDate ? `Due ${longDate(bill.dueDate)}` : 'No due date',
    fields: [
      { label: 'Open', value: formatMoney(bill.openBalance) },
      { label: 'Overdue', value: bill.dueDate ? `${daysOverdue(bill.dueDate, today)} days` : '—' },
    ],
    action: <ExcludeBox bill={bill} checked={excluded.has(bill.transactionId)} onToggle={toggle} />,
  }));

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile label="Overdue open bills" value={formatMoney(total)} hint={`${bills.length} bills past their due date`} />
        <StatTile label="Excluded" value={formatMoney(excludedTotal)} hint="Left out of the forecast" />
        <StatTile label="Still counted" value={formatMoney(total - excludedTotal)} hint="Paid in the first forecast week" />
      </div>
      <p className="mb-4 text-portal-small text-portal-muted">
        The forecast pays every overdue bill in the first week. Exclude any that are stale, disputed or already paid outside
        QuickBooks, so they stop pulling cash down.
      </p>
      {bills.length === 0 ? (
        <Panel>
          <EmptyState title="No overdue bills" message="Every open QuickBooks bill is still within its due date." />
        </Panel>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search bills"
              placeholder="Vendor"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
          {rows.length === 0 ? (
            <Panel>
              <EmptyState title="No bills match" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable
              headers={[
                'Exclude',
                { label: 'Vendor', sortKey: BILL_SORT_FIELDS.vendor },
                { label: 'Bill date', sortKey: BILL_SORT_FIELDS.billDate },
                { label: 'Due date', sortKey: BILL_SORT_FIELDS.dueDate },
                'Overdue',
                { label: 'Open amount', sortKey: BILL_SORT_FIELDS.amount },
              ]}
              rightAlignedHeaders={[4, 5]}
              sort={sort}
              onSort={onSort}
              minWidth={760}
              cards={cards}
            >
              {rows.map((bill) => (
                <tr
                  key={bill.transactionId}
                  className={cn('border-t border-portal-line align-middle', excluded.has(bill.transactionId) && 'bg-portal-bg')}
                >
                  <td className="p-3 text-portal-small">
                    <ExcludeBox bill={bill} checked={excluded.has(bill.transactionId)} onToggle={toggle} />
                  </td>
                  <td className="p-3 text-portal-small font-bold text-portal-ink">{bill.vendor}</td>
                  <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{longDate(bill.billDate)}</td>
                  <td className="whitespace-nowrap p-3 text-portal-small">{bill.dueDate ? longDate(bill.dueDate) : '—'}</td>
                  <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-rose-700">
                    {bill.dueDate ? `${daysOverdue(bill.dueDate, today)} days` : '—'}
                  </td>
                  <td
                    className={cn(
                      'whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums',
                      excluded.has(bill.transactionId) ? 'text-portal-muted line-through' : 'text-portal-ink',
                    )}
                  >
                    {formatMoney(bill.openBalance)}
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
          <div className="mt-4 space-y-3">
            {save.isError ? <ErrorNotice inline title="Could not save exclusions" message={errorMessage(save.error)} /> : null}
            {save.isSuccess && !dirty ? (
              <SavedNote message={`Saved. ${formatMoney(excludedTotal)} of overdue bills is now left out of the forecast.`} />
            ) : null}
            <Button
              type="button"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate({ excludedBillIds: [...excluded] })}
            >
              {save.isPending ? 'Saving…' : 'Save exclusions'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Analytics → Accounting → Assumptions: everything the cash forecast takes
 * from a person. This week's bank balances (per-account source switch),
 * accounts QuickBooks does not have, the forecast levers with their measured
 * defaults, the new-bookings projection, one-off entries, which overdue bills
 * to leave out, and who gets the cash emails. Each section saves on its own and turns
 * into its confirmation. Behind view_accounting.
 */
export function CashAssumptionsPage(): React.ReactElement {
  const assumptions = useAssumptions();
  // The values in use (payroll, commissions, benefits, ad spend, cost ratio) shown beside each field.
  const forecast = useCashFlow(CASH_SCENARIO.base);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Assumptions"
        subtitle="What the cash forecast takes from you: this week's balances, the levers, known one-offs, stale bills and who gets the cash emails."
      />
      {assumptions.isError ? (
        <ErrorNotice title="Assumptions unavailable" message={errorMessage(assumptions.error)} />
      ) : assumptions.isPending ? (
        <LoadingState label="Loading assumptions…" />
      ) : (
        <div className="space-y-8">
          <BankBalancesPanel view={assumptions.data} />
          <ManualAccountsPanel view={assumptions.data} />
          <PayrollPanel
            assumptions={assumptions.data.assumptions}
            breakdown={forecast.data?.payroll}
            parameters={forecast.data?.parameters}
          />
          <SettingsPanel assumptions={assumptions.data.assumptions} parameters={forecast.data?.parameters} />
          <NewBookingsPanel assumptions={assumptions.data.assumptions} parameters={forecast.data?.parameters} />
          <OneOffPanel assumptions={assumptions.data.assumptions} />
          <div>
            <SectionHeader
              title="Overdue bills"
              description="Open QuickBooks bills past their due date. Tick the ones the forecast should ignore."
            />
            <ExcludedBillsSection view={assumptions.data} />
          </div>
          <CashEmailsPanel assumptions={assumptions.data.assumptions} />
        </div>
      )}
    </section>
  );
}
